// Features that may require consent(s) to be given by user
const FEATURE_CONSENTS = [
  { id: 'dealers', cookieConsents: ['cookie_cat_marketing'], allowedConsentTypes: ['cookie', 'local', 'session'] },
  { id: 'rns', cookieConsents: ['cookie_cat_marketing'], allowedConsentTypes: ['cookie', 'local', 'session'] },
];

// Override cookie consent requirements for specified features
const ntConsents = window?.ntCookieConsents || {};
for (let id in ntConsents) {
  const feature = FEATURE_CONSENTS.find(item => item.id === id);
  if (feature) {
    feature.cookieConsents = ntConsents[id];
  }
}

/**
 * Class used to manage feature specific consents. Feature consent may be granted via CookieInformation or by adding manual consent to browser session/localstorage.
 *
 * Note! Each feature will provide UI for content management, this class provides shared functionality for custom consent management.
 */
class Consent {

  #storageKey = 'featureConsent';
  #consent = {};
  #listeners = [];

  constructor() {
    this.#consent = this.#loadConsent();

    window?.CookieInformation?.addCustomEventListenerForGTMConsentModeTemplate((consent) => {
      this.setConsent(this.#loadConsent());
    });
    window.addEventListener('storage', (e) => {
      if (e.key === this.#storageKey) {
        this.setConsent(this.#loadConsent());
      }
    });
  }

  #triggerEvent(name, data = []) {
    this.#listeners.filter(item => item.name === name).forEach(listener => {
      listener.callback.apply(null, data);
    });
  }

  addEventListener(name, callback) {
    this.#listeners.push({ name, callback });
  }

  removeEventListener(name, callback) {
    this.#listeners = this.#listeners.filter(item => item.name !== name || item.callback !== callback);
  }

  /**
   * Get active consent for every registed feature.
   *
   * @returns {object} Feature mapped to active consent type (or null if feature has no consent).
   */
  getConsent() {
    return this.#consent;
  }

  /**
   * Set feature consent(s).
   *
   * @param {object} consent Feature id mapped to consent type or null.
   */
  setConsent(consent = {}) {
    const oldConsent = this.#consent;
    consent = {
      ...oldConsent,
      ...consent,
    };
    if (JSON.stringify(oldConsent) !== JSON.stringify(consent)) {
      this.#consent = consent;
      this.#triggerEvent('change', [consent, oldConsent]);
    }
  }

  /**
   * Persist current consent configuration to browser storage.
   */
  updateConsent() {
    const sessionConsent = [];
    const localConsent = [];
    for (let id in this.#consent) {
      const consentType = this.#consent[id];
      if (consentType === 'session') {
        sessionConsent.push(id);
      }
      else if (consentType === 'local') {
        localConsent.push(id);
      }
    }
    window.sessionStorage.setItem(this.#storageKey, sessionConsent.join(','));
    window.localStorage.setItem(this.#storageKey, localConsent.join(','));
  }

  /**
   * Load consent state from storage.
   *
   * @returns {object} Feature id mapped to consent type or null.
   */
  #loadConsent() {
    const cookieConsent = this.#getExternalCookieConsent();
    const result = {};
    FEATURE_CONSENTS.forEach(consent => {
      result[consent.id] = consent.allowedConsentTypes.find(type => {
        if (type === 'cookie' && Array.isArray(consent.cookieConsents)) {
          return consent.cookieConsents.every(value => cookieConsent[value] === true);
        }
        else if (type === 'local') {
          const consents = window.localStorage.getItem(this.#storageKey)?.split(',') || [];
          return consents.includes(consent.id);
        }
        else if (type === 'session') {
          const consents = window.sessionStorage.getItem(this.#storageKey)?.split(',') || [];
          return consents.includes(consent.id);
        }

        return false;
      }) || null;
    });

    return result;
  }

  /**
   * Get external cookie consent.
   *
   * @returns {object} Cookie consent object.
   */
  #getExternalCookieConsent() {
    const externalCookies = window?.CookieInformation?.getStatusOfUsedConsentTypes() || {};
    const overrides = window.sessionStorage.getItem('cookieConsentOverrides');
    return {
      ...externalCookies,
      ...(overrides ? JSON.parse(overrides) : {}),
    };
  }

}

export default new Consent();
